import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChatStarter,
  ChatStartersResponse,
  CoachAuthError,
  getChatStarters,
  tapChatStarter,
} from "apps/user/learner/api/coach";
import {
  isTechnicalMessage,
  withOpeningLine,
} from "apps/user/learner/pages/chat/chat-starters";
import type { Message } from "apps/user/learner/pages/chat/types";
import { authenticationFailed } from "providers/apollo-client";
import { logError } from "utility/Function";

const NO_STARTERS: ChatStartersResponse = {
  opening_line: null,
  opening_line_id: null,
  starters: [],
  show_in_chat: false,
};

/** The newest message when Coach answered: anything after it starts the chat. */
interface Anchor {
  id: string | null;
  length: number;
}

interface Read {
  /** The chat the answer was read for. */
  epoch: number;
  answer: ChatStartersResponse;
  anchor: Anchor;
}

/** Which chat is on screen: a new one reads Coach's answer again. */
interface Epoch {
  n: number;
  conversationId: string | null;
  day: string;
  /** History has loaded once: the chat the page opened on is known. */
  settled: boolean;
}

interface Options {
  /** `ori_dynamic_starters`, and whether the flags have answered at all. */
  flagOn: boolean;
  flagsResolved: boolean;
  hasEverMessaged: boolean;
  isLoadingHistory: boolean;
  /** The disclaimer is accepted, so the chat is what the learner sees. */
  onScreen: boolean;
  /** A QA thread is on screen: its transcript is not the learner's chat. */
  qaMode: boolean;
  /** A past day is on screen. */
  readOnly: boolean;
  /** The page opened to send another screen's starter: that is the chat it
   *  opened on, not a later one. */
  arrivedWithStarter: boolean;
  conversationId: string | null;
  messages: Message[];
}

/**
 * A returning learner's own starters and Ori's opening line. Legacy web keeps
 * its own copy of the new web app's `useChatStarters` rules (this CRA app
 * cannot import `@wone/sdk`): Coach's `show_in_chat` is as of the chat
 * opening; the greeting is held while its answer is on the way and while it
 * leads; chat leads only until anything new reaches the transcript; the line
 * is drawn only once chat led with it and stays above the learner's own
 * words; one tap per showing, spent only once its message has gone; and a
 * new chat (a new conversation the learner did not just start, or a new day
 * whose chat is empty) reads again.
 */
export default function useOriPersonalStarters({
  flagOn,
  flagsResolved,
  hasEverMessaged,
  isLoadingHistory,
  onScreen,
  qaMode,
  readOnly,
  arrivedWithStarter,
  conversationId,
  messages,
}: Options) {
  const day = useLocalDay();
  const learnerSpoke = messages.some(
    (message) => message.role === "user" && !isTechnicalMessage(message)
  );

  // Decided as the page renders, so the greeting hold never sees a new chat
  // through an old chat's answer. A conversation the learner's own message
  // just created is that chat adopting its id, and midnight in a chat under
  // way is still that chat.
  const epochRef = useRef<Epoch>({
    n: 0,
    conversationId,
    day,
    settled: false,
  });
  const previous = epochRef.current;
  if (!previous.settled) {
    if (!isLoadingHistory) {
      epochRef.current = { n: 0, conversationId, day, settled: true };
    }
  } else if (
    !qaMode &&
    (previous.conversationId !== conversationId || previous.day !== day)
  ) {
    const adopted =
      previous.conversationId === null &&
      conversationId !== null &&
      learnerSpoke;
    const sameChat =
      previous.conversationId === conversationId &&
      messages.some((message) => !isTechnicalMessage(message));
    epochRef.current = {
      n: adopted || sameChat ? previous.n : previous.n + 1,
      conversationId,
      day,
      settled: true,
    };
  }
  const epoch = epochRef.current.n;
  // An entry-point arrival is the chat it opened on: a later one can lead.
  const [arrivalEpoch] = useState(arrivedWithStarter ? 0 : -1);
  const wanted = flagOn && hasEverMessaged && arrivalEpoch !== epoch;
  // Coach counts every read as a showing, so only a chat that can lead with
  // them reads them.
  const canRead = wanted && !isLoadingHistory && onScreen && !qaMode;

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [read, setRead] = useState<Read | null>(null);
  const readEpoch = read?.epoch ?? null;
  useEffect(() => {
    if (!canRead || readEpoch === epoch) return undefined;
    // A read cut short (the chat left, or a remount) is read again next time.
    let live = true;
    // The transcript as the read starts: Coach's answer is as of then, so
    // anything the learner sends while it is on its way starts the chat, and
    // a late answer that would lead is not shown.
    const at = messagesRef.current;
    const anchor: Anchor = {
      id: at[at.length - 1]?.message_id ?? null,
      length: at.length,
    };
    const apply = (answer: ChatStartersResponse) => {
      if (live) setRead({ epoch, answer, anchor });
    };
    getChatStarters()
      .then(apply)
      .catch((error) => {
        if (error instanceof CoachAuthError) {
          // The session is gone: the chat's own sign-in path takes over.
          authenticationFailed();
          return;
        }
        // Coach answers its own failures with the empty set, so a thrown
        // read is the network. Chat then has nothing to lead with and greets
        // as it would without the starters: the learner still gets Ori.
        logError("Ori chat starters fetch failed", error);
        apply(NO_STARTERS);
      });
    return () => {
      live = false;
    };
  }, [canRead, epoch, readEpoch]);

  const current = read?.epoch === epoch ? read : null;
  const answer = current?.answer ?? null;
  const anchor = current?.anchor ?? null;

  // The line goes under the anchor, so the transcript has to still hold it.
  const placeable =
    anchor !== null &&
    (anchor.id === null
      ? anchor.length === 0
      : messages.some((message) => message.message_id === anchor.id));
  // A QA thread's messages are not the learner's chat: nothing in them starts
  // it, and no line is drawn over them.
  const firstNew = useMemo(
    () => (anchor && !qaMode ? firstAfter(messages, anchor) : null),
    [anchor, qaMode, messages]
  );
  // Latched, so a re-paged or reconciled transcript cannot make chat lead again.
  const [started, setStarted] = useState<{
    epoch: number;
    first: Message;
  } | null>(null);
  useEffect(() => {
    if (firstNew && started?.epoch !== epoch) {
      setStarted({ epoch, first: firstNew });
    }
  }, [firstNew, started, epoch]);
  const firstSince =
    (started?.epoch === epoch ? started.first : null) ?? firstNew;

  const [taken, setTaken] = useState<{ epoch: number; id: string } | null>(
    null
  );
  const takenId = taken?.epoch === epoch ? taken.id : null;
  const starters = useMemo(
    () => (answer?.starters ?? []).filter((starter) => starter.id !== takenId),
    [answer, takenId]
  );
  // Coach says chat leads, and it has something to lead with.
  const leadsWithStarters =
    wanted &&
    answer !== null &&
    answer.show_in_chat &&
    answer.starters.length > 0 &&
    placeable;
  const leads =
    leadsWithStarters && firstSince === null && !qaMode && !readOnly;

  const [dismissedEpoch, setDismissedEpoch] = useState<number | null>(null);
  const dismiss = useCallback(() => setDismissedEpoch(epoch), [epoch]);
  const onScreenStarters = leads && dismissedEpoch !== epoch ? starters : [];

  // The line is drawn only once chat has led with it, starters and all: a
  // learner who types under an answer that did not lead never gets it.
  const [ledLine, setLedLine] = useState<{
    epoch: number;
    id: string;
    text: string;
  } | null>(null);
  const shownStarters = onScreenStarters.length > 0;
  useEffect(() => {
    if (
      !shownStarters ||
      ledLine?.epoch === epoch ||
      !answer?.opening_line ||
      !answer.opening_line_id
    ) {
      return;
    }
    setLedLine({
      epoch,
      id: answer.opening_line_id,
      text: answer.opening_line,
    });
  }, [shownStarters, ledLine, epoch, answer]);
  const line = ledLine?.epoch === epoch ? ledLine : null;
  // The learner's own words engage with the line, which then stays above
  // them; anything else first drops it.
  const engaged = firstSince?.role === "user";
  const lineShown =
    line && placeable && !qaMode && (leads || engaged) ? line : null;

  // One tap per showing: a second click, or one while the first waits for
  // the connection, does nothing.
  const claimedEpochRef = useRef<number | null>(null);
  const claim = useCallback(() => {
    if (claimedEpochRef.current === epoch) return false;
    claimedEpochRef.current = epoch;
    return true;
  }, [epoch]);
  /** The tapped starter's message has gone: Coach takes it off every screen. */
  const spend = useCallback(
    (starter: ChatStarter) => {
      setTaken({ epoch, id: starter.id });
      tapChatStarter(starter.id).catch((error) =>
        // The message already went and Ori is answering it: a lost report
        // only leaves this starter on other screens a while longer.
        logError("Ori chat starter tap failed", error)
      );
    },
    [epoch]
  );

  const withLine = useMemo(
    () => withOpeningLine(messages, lineShown, anchor),
    [messages, lineShown, anchor]
  );

  return {
    /** Coach greets an empty chat only when it has nothing of its own to open
     *  with: held until the flags and Coach's answer say otherwise. */
    holdsGreeting:
      !flagsResolved || (wanted && (answer === null || leadsWithStarters)),
    /** No greeting is coming: chat leads with these instead. */
    leadsWithStarters,
    /** The starters to show now. */
    starters: onScreenStarters,
    /** Names this showing, for its one impression. */
    showingKey: `personal-${conversationId ?? ""}-${day}-${epoch}-${
      anchor?.id ?? anchor?.length ?? ""
    }`,
    /** The line on screen, which the next message the learner sends names. */
    openingLineId: lineShown?.id ?? null,
    /** The live transcript with the line drawn in. */
    messages: withLine,
    claim,
    spend,
    dismiss,
  };
}

/** The local date, kept current across midnight. */
function useLocalDay() {
  const [day, setDay] = useState(localDay);
  useEffect(() => {
    const now = new Date();
    const midnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1
    );
    const timer = setTimeout(
      () => setDay(localDay()),
      midnight.getTime() - now.getTime()
    );
    return () => clearTimeout(timer);
  }, [day]);
  return day;
}

function localDay() {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

/** The first message the learner sees after `anchor`, or null. */
function firstAfter(messages: Message[], anchor: Anchor): Message | null {
  const from =
    anchor.id === null
      ? anchor.length
      : messages.findIndex((message) => message.message_id === anchor.id) + 1;
  if (anchor.id !== null && from === 0) return null;
  return (
    messages.slice(from).find((message) => !isTechnicalMessage(message)) ?? null
  );
}
